import { setToken, redirectByRole, getToken } from './auth';

const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.alderadvisor.com';

interface AuthResponse {
  access_token: string;
  detail?: string;
}

if (getToken()) {
  redirectByRole();
}

document.querySelectorAll('[data-tab]').forEach((btn) => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('[data-tab]').forEach((b) => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
    });
    document.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      (panel as HTMLElement).hidden = panel.id !== 'panel-' + (btn as HTMLElement).dataset.tab;
    });
  });
});

const showResult = (form: HTMLFormElement, success: boolean, text: string): void => {
  let el = form.querySelector<HTMLParagraphElement>('.form-result');
  if (!el) {
    el = document.createElement('p');
    el.className = 'form-result';
    form.appendChild(el);
  }
  el.textContent = text;
  el.dataset.state = success ? 'success' : 'error';
};

const handleAuth = (
  form: HTMLFormElement,
  endpoint: string,
  getPayload: (form: HTMLFormElement) => Record<string, unknown>,
  onSuccess: (data: AuthResponse) => void = (data) => { setToken(data.access_token); redirectByRole(); }
): void => {
  form.addEventListener('submit', (e: SubmitEvent) => {
    e.preventDefault();
    const btn = form.querySelector<HTMLButtonElement>('[type=submit]');
    if (!btn) return;

    btn.disabled = true;

    let payload: Record<string, unknown>;
    try {
      payload = getPayload(form);
    } catch (err) {
      showResult(form, false, (err as Error).message || 'Please fix the form and try again.');
      btn.disabled = false;
      return;
    }

    fetch(API_BASE + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) =>
        res.json().then((data: AuthResponse) => {
          if (!res.ok) throw new Error(data.detail || 'Request failed.');
          return data;
        })
      )
      .then(onSuccess)
      .catch((err: Error) => {
        showResult(form, false, err.message || 'Something went wrong. Please try again.');
        btn.disabled = false;
      });
  });
};

const forgotBtn = document.getElementById('forgot-pw-btn');
const forgotPanel = document.getElementById('forgot-pw-panel') as HTMLElement | null;
const forgotForm = document.querySelector<HTMLFormElement>('.form-grid--forgot');

if (forgotBtn && forgotPanel) {
  forgotBtn.addEventListener('click', () => {
    forgotPanel.hidden = false;
    forgotBtn.hidden = true;
  });
}

if (forgotForm) {
  forgotForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = forgotForm.querySelector<HTMLButtonElement>('[type=submit]');
    if (btn) btn.disabled = true;
    const email = forgotForm.querySelector<HTMLInputElement>('[name=email]')?.value || '';
    try {
      await fetch(API_BASE + '/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (forgotPanel) {
        forgotPanel.innerHTML =
          '<p class="form-result" data-state="success">If an account exists for that email, a reset link has been sent.</p>';
      }
    } catch {
      showResult(forgotForm, false, 'Something went wrong. Please try again.');
      if (btn) btn.disabled = false;
    }
  });
}

const signinForm = document.querySelector<HTMLFormElement>('.form-grid--signin');
if (signinForm) {
  handleAuth(signinForm, '/auth/login', (form: HTMLFormElement) => ({
    email: form.querySelector<HTMLInputElement>('[name=email]')?.value,
    password: form.querySelector<HTMLInputElement>('[name=password]')?.value,
  }));
}

const registerForm = document.querySelector<HTMLFormElement>('.form-grid--register');
if (registerForm) {
  handleAuth(
    registerForm,
    '/auth/register',
    (form: HTMLFormElement) => {
      const password = form.querySelector<HTMLInputElement>('[name=password]')?.value;
      const confirmPassword = form.querySelector<HTMLInputElement>('[name=confirm_password]')?.value;
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match.');
      }
      return {
        email: form.querySelector<HTMLInputElement>('[name=email]')?.value,
        password,
        confirm_password: confirmPassword,
        first_name: form.querySelector<HTMLInputElement>('[name=first_name]')?.value,
        last_name: form.querySelector<HTMLInputElement>('[name=last_name]')?.value,
        company_name: form.querySelector<HTMLInputElement>('[name=company]')?.value,
        phone: form.querySelector<HTMLInputElement>('[name=phone]')?.value || null,
      };
    },
    (data: AuthResponse) => {
      setToken(data.access_token);
      window.location.replace('verify-pending.html');
    },
  );
}
